/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Box, Heading } from 'grommet';
import { Modal as GModal, ModalProps as GModalProps, Button } from '@blend/alchemy';

import { theme } from './theme';
import { BrandColors, MaterialIcon, Text } from '@blend/enterprise-components';
import styled from 'styled-components';

export namespace Modal {
  export interface Props extends GModalProps {
    buttonComponents: React.ReactNode;
    handleBack?: () => void;
    handleClose: () => void;
    title?: string;
  }
}

const BackWrapper = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  gap: 4px;
  margin-right: auto;
  padding: 6px;
`;

const ButtonBox = styled(Box)`
  gap: 16px;
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  flex-grow: 1;
`;

// This should be re-written to come from Alchemy when Icon has the expected icon
// And with the Alchemy text component
const BackButton: React.FC<{ handleBack: () => void }> = ({ handleBack }) => {
  return (
    <BackWrapper id="it-backBtn" onClick={handleBack}>
      <MaterialIcon color={BrandColors.NIGHT_90} size={20} type="arrow_back" />
      <Text color={BrandColors.NIGHT_90} fontSize="md">
        Back
      </Text>
    </BackWrapper>
  );
};

export const Modal: React.FC<Modal.Props> = ({
  buttonComponents,
  handleBack,
  children,
  id,
  title,
  isOpen,
  handleClose,
  ...modalProps
}) => {
  return (
    <GModal
      ariaHideApp={false}
      buttons={
        <>
          <ButtonBox>
            {handleBack && <BackButton handleBack={handleBack} />}
            <Button id={`${id}-CancelBtn`} label="Cancel" onClick={handleClose} variant="secondary" />
            {buttonComponents}
          </ButtonBox>
        </>
      }
      buttonsBoxProps={{
        direction: 'row',
        flex: { grow: 1 },
        justify: 'between',
        ...modalProps.buttonsBoxProps,
      }}
      contentBoxProps={{
        overflow: 'auto',
        pad: 'medium',
        ...modalProps.contentBoxProps,
      }}
      headerProps={
        modalProps.headerProps ?? {
          boxProps: {
            margin: { vertical: 'small' },
          },
          showCloseButton: true,
          title: (
            <Heading level="2" size="small">
              {title}
            </Heading>
          ),
        }
      }
      id={id}
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={
        modalProps.style ?? {
          content: {
            height: 'fit-content',
            width: 'fit-content',
          },
        }
      }
      theme={theme}
    >
      {children}
    </GModal>
  );
};
